/**
 * Quas story timeline
 * Tracks the milestone being read in sections/quas-story-timeline.liquid:
 * the last year to pass the middle of the screen. The photo frame shows that
 * year's picture and stamps the year, and the rail fills down to it. Without
 * this script the frame keeps its first picture and the rail stays full.
 * With reduced motion the same states change without transitions (the
 * shared section layer shortens them), and the stamp does not animate.
 */
(function () {
  'use strict';

  if (customElements.get('quas-story-timeline')) return;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class QuasStoryTimeline extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.quas-story-milestones');
      this.milestones = Array.from(this.querySelectorAll('.quas-story-milestone'));
      if (!this.list || !this.milestones.length) return;
      this.shots = Array.from(this.querySelectorAll('.quas-story-timeline__shot'));
      this.stamp = this.querySelector('[data-stamp]');
      this.current = -2;
      this.frame = null;

      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };

      this.observer = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible === this.listening) return;
        this.listening = visible;
        window[visible ? 'addEventListener' : 'removeEventListener']('scroll', this.schedule, { passive: true });
        if (visible) this.schedule();
      });
      this.observer.observe(this);
      window.addEventListener('resize', this.schedule, { passive: true });

      this.classList.add('is-tracking');
      this.update();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    update() {
      const line = window.innerHeight * 0.5;
      let active = -1;
      this.milestones.forEach((milestone, index) => {
        if (milestone.getBoundingClientRect().top <= line) active = index;
      });

      const listHeight = this.list.offsetHeight || 1;
      let reach = 0;
      if (active >= 0) {
        const milestone = this.milestones[active];
        const year = milestone.querySelector('.quas-story-milestone__year');
        reach = milestone.offsetTop + (year ? year.offsetTop + year.offsetHeight / 2 : 0);
      }
      this.list.style.setProperty('--quas-story-rail', Math.min(reach / listHeight, 1).toFixed(4));

      if (active === this.current) return;
      this.current = active;

      this.milestones.forEach((milestone, index) => {
        milestone.classList.toggle('is-reached', index <= active);
        milestone.classList.toggle('is-current', index === active);
      });

      const shown = this.milestones[Math.max(active, 0)];
      const frame = shown.dataset.frame;
      this.shots.forEach((shot) => shot.classList.toggle('is-active', shot.dataset.frame === frame));

      if (this.stamp && this.stamp.textContent !== shown.dataset.year) {
        this.stamp.textContent = shown.dataset.year;
        if (!prefersReducedMotion() && this.stamp.animate) {
          this.stamp.animate(
            [
              { opacity: 0, transform: 'translate3d(0, 10px, 0)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
          );
        }
      }
    }
  }

  customElements.define('quas-story-timeline', QuasStoryTimeline);
})();
